import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Combobox, Input } from "../../../components/ui";
import type { NotificationUser } from "../types";

interface RecipientOption {
  value: string;
  label: string;
}

export interface RecipientPickerProps {
  users: NotificationUser[];
  selected: string[];
  loading: boolean;
  emptyLabel: string;
  label: string;
  onChange: (userIds: string[]) => void;
}

const SEARCH_THRESHOLD = 6;

export default function RecipientPicker({
  users,
  selected,
  loading,
  emptyLabel,
  label,
  onChange,
}: RecipientPickerProps) {
  const { t } = useTranslation("common");

  const [search, setSearch] = useState("");

  const options = useMemo<RecipientOption[]>(
    () => users.map((user) => ({ value: user.userId, label: user.name })),
    [users],
  );

  const value = useMemo(
    () => options.filter((option) => selected.includes(option.value)),
    [options, selected],
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (term.length === 0) return users;

    return users.filter(
      (user) =>
        user.name.toLowerCase().includes(term) ||
        (user.roleName ?? "").toLowerCase().includes(term),
    );
  }, [users, search]);

  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((item) => item !== id) : [...selected, id]);

  return (
    <div>
      <p className="mb-1.5 text-[11px] font-medium text-content-secondary max-sm:text-xs">{label}</p>

      <div className="hidden sm:block">
        <Combobox
          isMulti
          size="sm"
          isClearable
          isSearchable
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          isLoading={loading}
          isDisabled={loading}
          options={options}
          value={value}
          placeholder={t("monitoreoIa.units.custodyModal.recipientsPlaceholder")}
          noOptionsMessage={() => emptyLabel}
          onChange={(picked) => onChange(picked.map((option) => option.value))}
          menuPortalTarget={document.body}
          menuPosition="fixed"
        />
      </div>

      <div className="space-y-2 sm:hidden">
        {users.length > SEARCH_THRESHOLD ? (
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("monitoreoIa.notifications.searchUser", "Buscar usuario")}
            icon={<i className="ri-search-line" />}
            containerClassName="w-full"
          />
        ) : null}

        {loading || filtered.length === 0 ? (
          <p className="rounded-xl border border-line-soft bg-subtle px-3 py-3 text-xs text-content-secondary">
            {loading ? t("monitoreoIa.notifications.loading", "Cargando...") : emptyLabel}
          </p>
        ) : (
          <ul className="max-h-[42vh] divide-y divide-line-soft overflow-y-auto rounded-xl border border-line bg-surface">
            {filtered.map((user) => {
              const isPicked = selected.includes(user.userId);

              return (
                <li key={user.userId}>
                  <button
                    type="button"
                    aria-pressed={isPicked}
                    onClick={() => toggle(user.userId)}
                    className={`flex min-h-14 w-full items-center gap-3 px-3 py-3 text-left transition-colors ${
                      isPicked ? "bg-primary-500/10" : "hover:bg-subtle"
                    }`}
                  >
                    <span
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 ${
                        isPicked ? "border-primary-500 bg-primary-500" : "border-line"
                      }`}
                    >
                      {isPicked && <i className="ri-check-line text-xs text-on-primary" />}
                    </span>

                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-500/10 text-[13px] font-bold text-primary-500">
                      {user.name.charAt(0)}
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] font-semibold text-content">
                        {user.name}
                      </span>
                      {user.roleName && (
                        <span className="block truncate text-xs text-content-secondary">
                          {user.roleName}
                        </span>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
